import { BindingGroupModel, InputGroupModel } from '../models';
import { BindingGroupDto, ConditionDto, InputGroupDto } from '../types';
import { getTabStructureType } from './IotaTabExporter';
import { hasInvalidCheckboxConstant } from '../../iota_applications/misc/checkboxConstant';

export function validateConditions(
    conditions: ConditionDto[],
    savedGroups: InputGroupDto[],
    displayedGroups: InputGroupModel[],
    tags: { id: string | null }[]
) {
    const savedById = new Map(savedGroups.map(group => [group.inputGroupId, group]));
    const displayedById = new Map(displayedGroups.map(group => [group.inputGroupId, group]));
    const tagIds = new Set(tags.map(tag => tag.id));
    for (const condition of conditions) {
        const saved = savedById.get(condition.inputGroupId);
        const displayed = displayedById.get(condition.inputGroupId);
        if (!saved || !displayed || getTabStructureType(saved) !== getTabStructureType(displayed)) {
            throw new Error(
                `Condition "${condition.name}" requires a compatible saved and displayed input group. Restore the group or choose another destination.`
            );
        }
        if (!tagIds.has(condition.tagId)) {
            throw new Error(`Condition "${condition.name}" requires an available destination tag.`);
        }
    }
}

export function validateRows(
    rows: BindingGroupDto[],
    available: BindingGroupModel[],
    tagIds: ReadonlySet<string | null>,
    variableIds: ReadonlySet<string>
) {
    const availableById = new Map(available.map(row => [row.bindingGroupId, row]));
    const seen = new Set<string>();
    for (const row of rows) {
        const targetRow = availableById.get(row.bindingGroupId);
        if (!targetRow || seen.has(row.bindingGroupId)) {
            throw new Error(`Tab row "${row.bindingGroupId}" is incompatible with the displayed target layout.`);
        }
        seen.add(row.bindingGroupId);
        validateRows(row.childBindingGroups, targetRow.childBindingGroups, tagIds, variableIds);
        const inputsById = new Map(targetRow.inputs.map(input => [input.inputId, input]));
        for (const binding of row.bindings) {
            const input = inputsById.get(binding.inputId);
            if (!input) {
                throw new Error(`Field "${binding.inputId}" is unavailable in the displayed target tab.`);
            }
            const references = binding.type === 'tag' ? tagIds : binding.type === 'variable' ? variableIds : undefined;
            if (
                references &&
                ((typeof binding.value !== 'string' && binding.value !== null) || !references.has(binding.value))
            ) {
                throw new Error(`The ${binding.type} for field "${binding.inputId}" is unavailable in this project.`);
            }
            if (hasInvalidCheckboxConstant(input.meta, binding)) {
                throw new Error(`Field "${binding.inputId}" requires a true or false constant.`);
            }
        }
    }
}
